import React, { useState, useEffect } from 'react';
import { Users, Shield, Trash2, AlertTriangle } from 'lucide-react';
import api from '../api';

export default function Settings() {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const result = await api.users();
        setUsers(result || []);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  const ROLE_LABELS = {
    admin: 'Administrador',
    gerencia: 'Gerencia',
    compras: 'Compras',
    almacen: 'Almacén',
    analista: 'Analista',
  };

  return (
    <div>
      <div className="tab-buttons">
        <button className={`tab-btn ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          <Users size={14} style={{display:'inline',marginRight:'6px'}} />Usuarios y roles
        </button>
        <button className={`tab-btn ${tab === 'rules' ? 'active' : ''}`} onClick={() => setTab('rules')}>
          <Shield size={14} style={{display:'inline',marginRight:'6px'}} />Reglas de negocio
        </button>
      </div>

      {tab === 'users' && (
        <div className="data-table-container" data-testid="settings-users-table">
          <div className="data-table-header">
            <h3>Usuarios del sistema</h3>
          </div>
          {loading ? (
            <div className="loading-spinner"><div className="spinner" /></div>
          ) : users.length === 0 ? (
            <div className="empty-state"><Users size={48} /><h3>Sin usuarios registrados</h3><p>Crea una cuenta para comenzar.</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Fecha de registro</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u._id || i}>
                    <td style={{fontWeight:500}}>{u.name}</td>
                    <td style={{fontSize:'0.78rem'}}>{u.email}</td>
                    <td><span className="status-badge info"><span className="dot" />{ROLE_LABELS[u.role] || u.role}</span></td>
                    <td className="mono" style={{fontSize:'0.75rem'}}>{u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : '-'}</td>
                    <td><span className={`status-badge ${u.active !== false ? 'ok' : 'critical'}`}><span className="dot" />{u.active !== false ? 'Activo' : 'Inactivo'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'rules' && (
        <div data-testid="settings-business-rules-form">
          <div className="section-card">
            <h3>Fórmula de Demanda Proyectada</h3>
            <p style={{fontSize:'0.82rem',color:'hsl(var(--muted-foreground))',marginTop:'6px',lineHeight:'1.6'}}>
              <strong>DemandaProyectada</strong> = (TrimestrePonderado × 0.55) + (MismoMesAñoAnterior × 0.25) + (MesAnterior × 0.20)
            </p>
            <p style={{fontSize:'0.82rem',color:'hsl(var(--muted-foreground))',marginTop:'8px',lineHeight:'1.6'}}>
              <strong>Trimestre Ponderado</strong> = (T1 × 0.50) + (T2 × 0.30) + (T3 × 0.20)
            </p>
          </div>

          <div className="section-card">
            <h3>Cálculo del Pedido Sugerido</h3>
            <p style={{fontSize:'0.82rem',color:'hsl(var(--muted-foreground))',marginTop:'6px',lineHeight:'1.6'}}>
              <strong>Stock Neto</strong> = Inventario Actual + Tránsito - Comprometido<br/>
              <strong>Stock Objetivo</strong> = (Demanda Diaria × Horizonte) + Stock Seguridad<br/>
              <strong>Horizonte</strong> = Lead Time + Período de Revisión<br/>
              <strong>Pedido Sugerido</strong> = max(0, Stock Objetivo - Stock Neto)
            </p>
          </div>

          <div className="section-card">
            <h3>Umbrales de Homologación</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',marginTop:'12px'}}>
              <div style={{padding:'12px',borderRadius:'8px',background:'hsl(152 63% 33% / 0.06)',textAlign:'center'}}>
                <div style={{fontSize:'0.72rem',color:'hsl(var(--muted-foreground))'}}>Alta confianza</div>
                <div style={{fontFamily:'Space Grotesk',fontSize:'1.3rem',fontWeight:700,color:'hsl(152,63%,33%)'}}>≥ 92%</div>
                <div style={{fontSize:'0.7rem',color:'hsl(var(--muted-foreground))'}}>Auto-aprobado</div>
              </div>
              <div style={{padding:'12px',borderRadius:'8px',background:'hsl(35 92% 45% / 0.06)',textAlign:'center'}}>
                <div style={{fontSize:'0.72rem',color:'hsl(var(--muted-foreground))'}}>Media confianza</div>
                <div style={{fontFamily:'Space Grotesk',fontSize:'1.3rem',fontWeight:700,color:'hsl(35,92%,45%)'}}>80-91%</div>
                <div style={{fontSize:'0.7rem',color:'hsl(var(--muted-foreground))'}}>Revisión rápida</div>
              </div>
              <div style={{padding:'12px',borderRadius:'8px',background:'hsl(0 72% 48% / 0.06)',textAlign:'center'}}>
                <div style={{fontSize:'0.72rem',color:'hsl(var(--muted-foreground))'}}>Baja confianza</div>
                <div style={{fontFamily:'Space Grotesk',fontSize:'1.3rem',fontWeight:700,color:'hsl(0,72%,48%)'}}>&lt; 80%</div>
                <div style={{fontSize:'0.7rem',color:'hsl(var(--muted-foreground))'}}>Revisión obligatoria</div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <h3>Factores de Ajuste</h3>
            <table className="data-table" style={{margin:'12px 0 0 0'}}>
              <thead><tr><th>Escenario</th><th>Factor de Tendencia</th><th>Acción</th></tr></thead>
              <tbody>
                <tr><td>Crecimiento acelerado</td><td className="mono">1.10 - 1.25</td><td>Subir pedido, ampliar safety stock</td></tr>
                <tr><td>Estable</td><td className="mono">0.98 - 1.05</td><td>Mantener forecast promedio</td></tr>
                <tr><td>Caída temporal</td><td className="mono">0.90 - 0.98</td><td>Bajar con cautela, usar trimestre como ancla</td></tr>
                <tr><td>Caída fuerte</td><td className="mono">0.85 - 0.90</td><td>Restricción agresiva</td></tr>
                <tr><td>Pico atípico</td><td className="mono">0.85 - 0.95 (anomaly)</td><td>Cap del dato, no usar pico completo</td></tr>
              </tbody>
            </table>
          </div>

          <div className="section-card" style={{borderColor:'hsl(0 72% 48% / 0.3)'}}>
            <h3 style={{color:'hsl(0,72%,48%)',display:'flex',alignItems:'center',gap:'8px'}}>
              <Trash2 size={16} /> Zona de peligro
            </h3>
            <p style={{fontSize:'0.82rem',color:'hsl(var(--muted-foreground))',marginTop:'6px',marginBottom:'12px'}}>
              Eliminar todos los datos (catálogo, inventario, ventas, homologación). Las cuentas de usuario se mantienen.
            </p>
            {!showResetConfirm ? (
              <button className="btn btn-danger" onClick={() => setShowResetConfirm(true)}>
                <Trash2 size={14} /> Limpiar todos los datos
              </button>
            ) : (
              <div style={{background:'hsl(0 72% 48% / 0.06)',padding:'16px',borderRadius:'8px'}}>
                <p style={{fontSize:'0.85rem',fontWeight:600,color:'hsl(0,72%,48%)',marginBottom:'8px',display:'flex',alignItems:'center',gap:'6px'}}>
                  <AlertTriangle size={16} /> ¿Estás seguro? Esta acción no se puede deshacer.
                </p>
                <div style={{display:'flex',gap:'8px'}}>
                  <button className="btn btn-danger" onClick={async () => {
                    setResetting(true);
                    try {
                      await api.reset();
                      window.location.href = '/';
                    } catch(e) { console.error(e); }
                    setResetting(false);
                  }} disabled={resetting}>
                    {resetting ? 'Limpiando...' : 'Sí, eliminar todo'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => setShowResetConfirm(false)}>Cancelar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
